/**
 * KMMO kit additions for the Skills screen (kept out of the kit's own files): the list row
 * (round icon, name, blue sub line, key cap or tag on the right) and a small group header.
 * Styles: ./extra-talents.css (prefix km-xrow / km-xgroup).
 */
import { el, onPress } from '../../hud/dom';
import { type IconSpec, iconEl } from './components';
import { kmIcon } from './icons';
import './extra-talents.css';

export interface SkillRowOptions {
  icon: IconSpec;
  name: string;
  /** Second line ("30 Rage", "1 pt", "Unlocks at 5 points"). */
  sub?: string;
  subTone?: 'blue' | 'muted' | 'gold' | 'bad';
  /** Key cap on the right ("1", "Q"). */
  keyCap?: string;
  /** Tag on the right instead of a key cap ("Learned"). */
  tag?: string;
  tagTone?: 'gold' | 'muted';
  /** Lock glyph instead of the icon; row dimmed. */
  locked?: boolean;
  /** Icon greyed (not learned / not usable) but row still bright. */
  dimIcon?: boolean;
  /** Gold ring on the icon (learned / owned). */
  goldRing?: boolean;
  selected?: boolean;
  onClick?: () => void;
  title?: string;
}

export interface SkillRowHandle {
  el: HTMLButtonElement;
  setSelected(on: boolean): void;
}

/** One row of the skill / talent list (V4 skill screen). */
export function skillRow(o: SkillRowOptions, parent?: HTMLElement): SkillRowHandle {
  const b = el('button', `km-xrow${o.locked ? ' locked' : ''}`, parent);
  b.type = 'button';
  const ic = el('span', `km-xrow-icon${o.goldRing ? ' gold' : ''}${o.dimIcon || o.locked ? ' dim' : ''}`, b);
  if (o.locked) ic.appendChild(kmIcon('lock', 'km-xrow-lock'));
  else ic.appendChild(iconEl(o.icon, 'km-xrow-img'));
  const t = el('span', 'km-xrow-text', b);
  el('span', 'km-xrow-name', t, o.name);
  if (o.sub) el('span', `km-xrow-sub ${o.subTone ?? 'blue'}`, t, o.sub);
  if (o.keyCap) el('span', 'km-xrow-key km-num', b, o.keyCap);
  else if (o.tag) el('span', `km-xrow-tag ${o.tagTone ?? 'gold'}`, b, o.tag);
  if (o.title) b.title = o.title;
  const setSelected = (on: boolean): void => {
    b.classList.toggle('selected', on);
    b.setAttribute('aria-pressed', String(on));
  };
  setSelected(!!o.selected);
  if (o.onClick) onPress(b, () => o.onClick?.());
  return { el: b, setSelected };
}

/** Thin group header inside a list ("Tier II", with a muted lock note on the right). */
export function listGroupHeader(label: string, note = '', parent?: HTMLElement): HTMLElement {
  const h = el('div', `km-xgroup${note ? ' locked' : ''}`, parent);
  el('span', 'km-xgroup-label', h, label);
  if (note) {
    const n = el('span', 'km-xgroup-note', h);
    n.appendChild(kmIcon('lock'));
    el('span', '', n, note);
  }
  return h;
}
